import { useState, useEffect, useRef } from 'react';
import { getWageAndPayTypes, getPayrollSetupById, getCorporations } from '../api';
import { ITEM_TYPE, ITEM_TYPE_OPTIONS } from '../Enums/Enum';

const getItemTypeString = (id) => {
    const found = ITEM_TYPE_OPTIONS.find(opt => opt.id === id);
    return found ? found.value : "";
};

const initialFormValues = {
    corporation: { corpName: "--Select--", corpValue: "" },
    itemName: "",
    itemType: "",
    payType: "",
    wageType: "",
    wageTypeName: "",
    wageSourceID: "",
    wageSourceType: "",
    paySourceID: "",
    paySourceType: "",
    updateCOA: false,
    updateCOADate: null,
    clone: false,
    cloningCorporations: [],
    active: true,
    entries: [{ department: "", jobTitle: "", expenseCoa: "", payableCoa: "" }],
};

export const usePayrollItemData = (open, editData, corpId, token,prefillData, showErrorPopup) => {
    const [corporations, setCorporations] = useState([{ corpName: "--Select--", corpValue: "" }]);
    const [wageTypes, setWageTypes] = useState([]);
    const [payTypes, setPayTypes] = useState([]);
    const [formValues, setFormValues] = useState(initialFormValues);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const hasFetchedDetails = useRef(false);

    useEffect(() => {
        if (open) hasFetchedDetails.current = false;
    }, [open, editData?.payrollItemId]);

    useEffect(() => {
        if (!open) return;

        const loadInitialData = async () => {
            if (editData && editData.payrollItemId && hasFetchedDetails.current) return;

            setDetailsLoading(true);
            try {
                // console.log("Loading initial data with corpId:", corporations);
                const corpPromise = corporations.length > 1
                    ? Promise.resolve(corporations)
                    : getCorporations(token);

                const typesPromise = wageTypes.length > 0
                    ? Promise.resolve([...wageTypes, ...payTypes])
                    : getWageAndPayTypes(token);

                const promises = [corpPromise, typesPromise];

                if (editData?.payrollItemId) {
                    promises.push(getPayrollSetupById(editData.payrollItemId, token));
                }

                const [corpData, typesData, detailsResponse] = await Promise.all(promises);

                let formattedCorps = corpData;
                if (corporations.length <= 1) {
                    formattedCorps = [{ corpName: "--Select--", corpValue: "" }, ...(corpData || []).map(c => ({
                        corpName: c.Name,
                        corpValue: c.ID
                    }))];
                    setCorporations(formattedCorps);
                }

                const wTypes = (typesData || []).filter(x => x.SourceType === 51);
                const pTypes = (typesData || []).filter(x => x.SourceType === 52);
                setWageTypes(wTypes);
                setPayTypes(pTypes);

                if (editData && editData.payrollItemId && detailsResponse) {
                    const response = detailsResponse;
                    const selectedCorp = formattedCorps.find(c => c.corpValue === response.CorporationID) || { corpName: "--Select--", corpValue: "" };
                    const selectedWage = wTypes.find(wt => wt.ID === response.WageTypeID);

                    const loadedValues = {
                        id: response.Id || response.ID || response.PayrollItemID,
                        payrollItemId: response.Id || response.PayrollItemID || response.ID,
                        corporation: selectedCorp,
                        itemName: response.ItemName || "",
                        itemType: getItemTypeString(response.ItemType),
                        wageTypeName: selectedWage ? selectedWage.Value : "",
                        wageType: response.WageTypeID || "",
                        payType: response.PayTypeID || "",
                        wageSourceID: response.WageSourceID || "",
                        wageSourceType: response.WageSourceType || "",
                        paySourceID: response.PaySourceID || "",
                        paySourceType: response.PaySourceType || "",
                        updateCOA: response.IsCOAUpdate ?? false,
                        updateCOADate: response.CoaupdateEffectiveDate ?? null,
                        clone: false,
                        active: response.IsActive === 1,
                        entries: (response.PayrollDeptJobDetails && response.PayrollDeptJobDetails.length > 0)
                            ? response.PayrollDeptJobDetails.map(d => ({
                                id: d.Id,
                                department: d.IncDeptID,
                                departmentName: d.IncDeptName || "",
                                jobTitle: d.JobID,
                                jobTitleName: d.JobTitleName || d.JobTitle || "",
                                expenseCoa: d.ExpenseAccountID || d.AccountID,
                                expenseCoaName: d.ExpenseAccountName || d.AccountName || "",
                                payableCoa: d.LiabilityAccountID,
                                payableCoaName: d.LiabilityAccountName || d.LiabilityAccName || "",
                                isActive: d.IsActive === 1,
                                isUsedEmp: d.IsUsedEMP || false
                            }))
                            : [{ department: "", jobTitle: "", expenseCoa: "", payableCoa: "", isActive: true }]
                    };

                    if (response.ItemType === ITEM_TYPE.Wages) {
                        loadedValues.wageType = response.WageTypeID || "";
                        loadedValues.payType = response.PayTypeID || "";
                    }
                    console.log("Loaded Values", loadedValues);
                    setFormValues(loadedValues);
                    hasFetchedDetails.current = true;
                } else if (!editData || prefillData) {
                    const urlCorpID = corpId;
                    let defaultCorp = initialFormValues.corporation;

                    if (urlCorpID && urlCorpID !== "000000000000000000000000000000000000" && formattedCorps.length > 1) {
                        const matched = formattedCorps.find(c => String(c.corpValue || "").toLowerCase() === String(urlCorpID || "").toLowerCase());
                        if (matched) defaultCorp = matched;
                    }

                    setFormValues({
                        ...initialFormValues,
                        corporation: defaultCorp
                    });
                }
                     } catch (error) {
                console.error("Error loading initial data:", error);
                if (editData && showErrorPopup) {
                    showErrorPopup("Failed to load details for editing.");
                }
            } finally {
                setDetailsLoading(false);
            }
        };

        loadInitialData();
    }, [open, editData, corpId, token, prefillData, showErrorPopup]);

    return { corporations, wageTypes, payTypes, formValues, detailsLoading };
};
